'use client';

import React, { useState, useEffect, useRef } from 'react';
import styles from './GoogleMapLazy.module.css';

interface GoogleMapLazyProps {
  title?: string;
  className?: string;
}

/**
 * Performance-optimized Google Maps component
 * - Uses Intersection Observer to load only when in viewport
 * - Facade pattern: shows placeholder until user clicks
 * - Lazy loading iframe
 * - Zero performance impact until user interaction
 */
export function GoogleMapLazy({ title = 'Jamezz B.V. - Breda', className }: GoogleMapLazyProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Intersection Observer: only load when component comes into view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Stop observing once visible
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleLoadMap = () => {
    setIsLoaded(true);
    
    // Track map interaction
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'map_interaction',
        map_action: 'load',
      });
    }
  };

  return (
    <div ref={containerRef} className={`${styles.mapContainer} ${className || ''}`}>
      {!isLoaded && isVisible && (
        <button
          className={styles.mapPlaceholder}
          onClick={handleLoadMap}
          aria-label="Laad Google Maps"
        >
          <div className={styles.placeholderContent}>
            <svg
              className={styles.mapIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                fill="currentColor"
              />
            </svg>
            <span className={styles.placeholderText}>Klik om kaart te laden</span>
            <span className={styles.placeholderSubtext}>Druivenstraat 25, 4816 KB Breda</span>
          </div>
        </button>
      )}

      {isLoaded && (
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.29311903775!2d4.806538976883383!3d51.59951760396937!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c46ce28c324997%3A0x23bc94be619abf99!2sJamezz%20B.V.!5e0!3m2!1snl!2snl!4v1715892817140!5m2!1snl!2snl"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={title}
          className={styles.mapIframe}
        />
      )}

      {!isVisible && (
        <div className={styles.mapSkeleton}>
          <div className={styles.skeletonContent}>Kaart wordt geladen...</div>
        </div>
      )}
    </div>
  );
}


